import { focusRingOutline } from '../../Themes/base/focusRing';
import { StyleObject } from 'styletron-react';
import {
	borderColor,
	borderRadius,
	borderStyle,
	borderWidth,
	marginSize,
	paddingSize,
} from '../../Utilities/utils/Theming';
import { IDummyProps } from './types';

const roundersHeader: { [key: string]: string } = {
	rounded: 'var(--epods--border-radius)',
	'rounded-sm': 'var(--epods--border-radius-sm)',
	'rounded-lg': 'var(--epods--border-radius-lg)',
	'rounded-0': '0px',
	'rounded-first': 'var(--epods--border-radius)',
	'rounded-sm-first': 'var(--epods--border-radius-sm)',
	'rounded-lg-first': 'var(--epods--border-radius-lg)',
};

const roundersContent: { [key: string]: string } = {
	rounded: 'var(--epods--border-radius)',
	'rounded-sm': 'var(--epods--border-radius-sm)',
	'rounded-lg': 'var(--epods--border-radius-lg)',
	'rounded-0': '0px',
	'rounded-last': 'var(--epods--border-radius)',
	'rounded-sm-last': 'var(--epods--border-radius-sm)',
	'rounded-lg-last': 'var(--epods--border-radius-lg)',
};

const sizes: { [key: string]: string } = {
	mini: 'var(--epods--space-inset-s)',
	compact: 'var(--epods--space-inset-s2)',
	default: 'var(--epods--space-inset-m)',
	large: 'var(--epods--space-inset-m2)',
};

const InputContainerError = {
	...borderColor('var(--epods--color-signal-error-border)'),
	backgroundColor: 'var(--epods--color-signal-error-bg)',
};

const InputContainerWarning = {
	...borderColor('var(--epods--color-signal-warning-border)'),
	backgroundColor: 'var(--epods--color-signal-warning-bg)',
};

const InputContainerPositive = {
	...borderColor('var(--epods--color-signal-success-border)'),
	backgroundColor: 'var(--epods--color-signal-success-bg)',
};

const InputContainerInfo = {
	...borderColor('var(--epods--color-signal-info-border)'),
	backgroundColor: 'var(--epods--color-signal-info-bg)',
};

const InputContainerNeutral = {
	backgroundColor: 'var(--epods--color-bg)',
	...borderColor('var(--epods--color-border)'),
};
const InputContainerDefault = {
	backgroundColor: 'var(--epods--color-bg-second-elevation)',
	...borderColor('var(--epods--color-border-light)'),
};
const InputContainerPhantom = {
	backgroundColor: 'transparent',
	...borderColor('none'),
};

const themes: { [key: string]: any } = {
	default: InputContainerDefault,
	neutral: InputContainerNeutral,
	positive: InputContainerPositive,
	negative: InputContainerError,
	info: InputContainerInfo,
	warning: InputContainerWarning,
	phantom: InputContainerPhantom,
};

export const PanelContainerStyles = ({ rounded = 'rounded-0', theme = 'default', secondLevel, ...props }: any) => {
	const isPresetRounders = ['rounded', 'rounded-0', 'rounded-sm', 'rounded-lg'].includes(rounded);
	const isPresetLastRounders = ['rounded-last', 'rounded-sm-last', 'rounded-lg-last'].includes(rounded);
	const isPresetFirstRounders = ['rounded-first', 'rounded-sm-first', 'rounded-lg-first'].includes(rounded);
	const roundersDefinition = isPresetRounders
		? borderRadius(`calc(${roundersContent[rounded]} + 1px)`)
		: isPresetFirstRounders
		? {
				borderTopLeftRadius: `calc(${roundersHeader[rounded]} + 1px)`,
				borderTopRightRadius: `calc(${roundersHeader[rounded]} + 1px)`,
				borderBottomRightRadius: '1px',
				borderBottomLeftRadius: '1px',
		  }
		: isPresetLastRounders
		? {
				borderTopLeftRadius: '1px',
				borderTopRightRadius: '1px',
				borderBottomRightRadius: `calc(${roundersContent[rounded]} + 1px)`,
				borderBottomLeftRadius: `calc(${roundersContent[rounded]} + 1px)`,
		  }
		: borderRadius(rounded);

	const isPresetTheme = ['default', 'neutral', 'positive', 'negative', 'info', 'warning', 'phantom'].includes(theme);
	const themeDefinition = isPresetTheme ? themes[theme] : theme;

	const _shadow =
		props.hideShadow || theme == 'phantom'
			? {
					boxShadow: 'none',
			  }
			: {
					boxShadow: '0px 2px 4px rgba(94, 124, 143, 0.15)',
			  };
	const _border = secondLevel
		? {
				...borderStyle('solid'),
				borderTopWidth: props.hideBorderTop ? '0' : '1px',
				borderRightWidth: '0',
				borderBottomWidth: props.hideBorderBottom ? '0' : '1px',
				borderLeftWidth: '0',
		  }
		: props.hideBorder || theme == 'phantom'
		? {
				...borderWidth('0px'),
		  }
		: {
				...borderStyle('solid'),
				borderTopWidth: props.hideBorderTop ? '0' : '1px',
				borderBottomWidth: props.hideBorderBottom ? '0' : '1px',
				borderRightWidth: props.hideBorderRight ? '0' : '1px',
				borderLeftWidth: props.hideBorderLeft ? '0' : '1px',
		  };
	return {
		style: ({ $theme }: any) => {
			return {
				//TODO: Change hex/rgba colors for color palette variables
				..._border,
				..._shadow,
				...roundersDefinition,
				...themeDefinition,
				backgroundColor: 'transparent',
			} as StyleObject;
		},
	};
};

export const panelContentStyles = ({ rounded = 'rounded-0', secondLevel, ...props }: IDummyProps) => {
	const isPresetRounders = [
		'rounded',
		'rounded-0',
		'rounded-sm',
		'rounded-lg',
		'rounded-last',
		'rounded-sm-last',
		'rounded-lg-last',
	].includes(rounded);
	const roundersDefinition = isPresetRounders ? roundersContent[rounded] : rounded;

	return {
		style: ({ $theme }: any) => {
			return {
				...paddingSize('0', '0', '0', '0'),
				borderBottomRightRadius: roundersDefinition,
				borderBottomLeftRadius: roundersDefinition,
				transitionDuration: '0s',
				backgroundColor: props.contentBackground || 'transparent',
				color: $theme.panel.panelTextColor,
			} as StyleObject;
		},
	};
};

export const contentStyles = (
	{ rounded = 'rounded-0', size = 'default', secondLevel, transparentContent, ...props }: IDummyProps,
	$theme: any,
) => {
	const isPresetRounders = [
		'rounded',
		'rounded-0',
		'rounded-sm',
		'rounded-lg',
		'rounded-last',
		'rounded-sm-last',
		'rounded-lg-last',
	].includes(rounded);
	const roundersDefinition = isPresetRounders ? roundersContent[rounded] : rounded;

	const isPresetSizes = ['mini', 'compact', 'default', 'large'].includes(size);
	const sizesDefinition = isPresetSizes ? sizes[size] : size;
	const _separator = props.hideSeparator
		? {
				borderTopWidth: '0px',
		  }
		: {
				borderTopColor: 'var(--epods--color-border-light)',
				borderTopStyle: 'solid',
				borderTopWidth: '1px',
		  };
	const horizontalSpacing = props.hideHorizontalSpacing ? '0' : size === 'large' ? sizes['default'] : sizesDefinition;
	const verticalSpacing = props.hideVerticalSpacing ? '0' : size === 'large' ? sizes['default'] : sizesDefinition;

	return {
		...marginSize('0', '0', verticalSpacing, '0'),
		...paddingSize(props.hideSeparator ? '0' : verticalSpacing, horizontalSpacing, '0', horizontalSpacing),
		..._separator,
		borderBottomRightRadius: `calc(${roundersDefinition} + 1px)`,
		borderBottomLeftRadius: `calc(${roundersDefinition} + 1px)`,
		color: 'var(--epods--color-primary)',
		fontFamily: 'var(--epods--font-base)',
		fontSize: 'var(--epods--font-size-h6)',
		fontWeight: 'normal', // reset
		lineHeight: 'var(--epods--line-height-p)',
		backgroundColor: transparentContent && 'transparent',
	} as StyleObject;
};

export const panelHeaderStyles = (
	{ rounded = 'rounded-0', size = 'default', secondLevel, ...props }: any,
	HTMLAttrs: any,
) => {
	const ellipsis = props.headerEllipsis
		? {
				whiteSpace: 'nowrap',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
		  }
		: {};

	const isPresetRounders = ['rounded', 'rounded-0', 'rounded-sm', 'rounded-lg'].includes(rounded);
	const isPresetLastRounders = ['rounded-last', 'rounded-sm-last', 'rounded-lg-last'].includes(rounded);
	const isPresetFirstRounders = ['rounded-first', 'rounded-sm-first', 'rounded-lg-first'].includes(rounded);

	const roundersDefinition = isPresetRounders
		? borderRadius(`calc(${roundersContent[rounded]} + 1px)`)
		: isPresetFirstRounders
		? {
				borderTopLeftRadius: `calc(${roundersHeader[rounded]} + 1px)`,
				borderTopRightRadius: `calc(${roundersHeader[rounded]} + 1px)`,
				borderBottomRightRadius: '1px',
				borderBottomLeftRadius: '1px',
		  }
		: isPresetLastRounders
		? {
				borderTopLeftRadius: '1px',
				borderTopRightRadius: '1px',
				borderBottomRightRadius: `calc(${roundersContent[rounded]} + 1px)`,
				borderBottomLeftRadius: `calc(${roundersContent[rounded]} + 1px)`,
		  }
		: borderRadius(rounded);

	const isPresetSizes = ['mini', 'compact', 'default', 'large'].includes(size);
	const sizesDefinition = isPresetSizes ? sizes[size] : size;
	const horizontalSpacing = props.hideHorizontalSpacing ? '0' : size === 'large' ? sizes['default'] : sizesDefinition;
	const verticalSpacing = props.hideVerticalSpacing ? '0' : size === 'large' ? sizes['default'] : sizesDefinition;

	return {
		style: ({ $theme, $isFocused }: any) => {
			return {
				color: secondLevel ? $theme.panel.subPanelTextColor : $theme.panel.panelTextColor,
				fontSize: secondLevel ? $theme.panel.subPanelHeaderTextSize : $theme.panel.panelHeaderTextSize,
				...borderWidth('0'),
				justifyContent: props.chevronPlacement == 'right' ? 'space-between' : 'flex-end',
				flexFlow: props.chevronPlacement == 'right' ? 'row' : 'row-reverse',
				outline: $isFocused ? '2px solid ' + $theme.colors.focus : undefined,
				outlineOffset: '0',
				...roundersDefinition,
				//...ellipsis, //TODO: to implement like card, need layout
				backgroundColor: secondLevel
					? 'rgba(255, 255, 255, 0.5)'
					: props.headerBackground
					? props.headerBackground
					: 'transparent',
				...paddingSize(sizesDefinition, horizontalSpacing, sizesDefinition, horizontalSpacing),
				':hover': {
					color: $theme.panel.panelTextColor,
				},
				':focus': {
					...focusRingOutline,
				},
			} as StyleObject;
		},
	};
};
